import React, { useState } from 'react'
import {
  Button,
  Text,
  Flex,
  Loader,
  IconButton,
  Tooltip,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
} from '@vibe/core'
import { Edit, Delete } from '@vibe/icons'
import type { UserInfo } from '../../types'
import { UserRole } from '../../types'

interface UserTableProps {
  users: UserInfo[]
  loading: boolean
  onLoadMore?: () => void
  onEditUser: (user: UserInfo) => void
  onDeleteUser: (userId: string) => Promise<void>
  totalItems: number
  hasMore: boolean
  currentUserId: string
  canManageUsers: boolean
}

/**
 * Status badge component
 */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getConfig = () => {
    const configs = {
      active: { className: 'bg-green-100 text-green-800', text: 'Activo' },
      inactive: { className: 'bg-red-100 text-red-800', text: 'Inactivo' },
      pending_confirmation: {
        className: 'bg-yellow-100 text-yellow-800',
        text: 'Pendiente',
      },
    }
    return (
      configs[status as keyof typeof configs] || {
        className: 'bg-gray-100 text-gray-800',
        text: status,
      }
    )
  }

  const config = getConfig()

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.text}
    </span>
  )
}

/**
 * Role badge component
 */
const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const getConfig = () => {
    const configs = {
      [UserRole.ADMINISTRADOR]: {
        className: 'bg-purple-100 text-purple-800',
        text: 'Administrador',
      },
      [UserRole.EDITOR]: {
        className: 'bg-blue-100 text-blue-800',
        text: 'Editor',
      },
      [UserRole.VISUALIZADOR]: {
        className: 'bg-gray-100 text-gray-800',
        text: 'Visualizador',
      },
    }
    return (
      configs[role as keyof typeof configs] || {
        className: 'bg-gray-100 text-gray-800',
        text: role,
      }
    )
  }

  const config = getConfig()

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {config.text}
    </span>
  )
}

/**
 * User table component with filtering, sorting, and bulk actions
 */
export const UserTable: React.FC<UserTableProps> = ({
  users = [],
  loading,
  onLoadMore,
  onEditUser,
  onDeleteUser,
  totalItems,
  hasMore,
  currentUserId,
  canManageUsers,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Ensure users is always an array
  const safeUsers = users || []

  /**
   * Handle delete user confirmation
   */
  const handleDeleteClick = (user: UserInfo) => {
    setUserToDelete(user)
    setShowDeleteConfirm(true)
  }

  /**
   * Confirm delete user
   */
  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        setIsDeleting(true)
        await onDeleteUser(userToDelete.id)
        setShowDeleteConfirm(false)
        setUserToDelete(null)
      } catch (error) {
        // Error handling is done in the parent component
        console.error('Error deleting user:', error)
      } finally {
        setIsDeleting(false)
      }
    }
  }

  /**
   * Cancel delete user
   */
  const cancelDeleteUser = () => {
    if (!isDeleting) {
      setShowDeleteConfirm(false)
      setUserToDelete(null)
    }
  }

  /**
   * Check if user can be deleted
   */
  const canDeleteUser = (user: UserInfo) => {
    // Cannot delete self
    if (user.id === currentUserId) return false

    // Cannot delete if not admin
    if (!canManageUsers) return false

    // TODO: Add check for last admin
    return true
  }

  if (safeUsers.length === 0 && !loading) {
    return (
      <div className="p-8 text-center">
        <Text>No se encontraron usuarios.</Text>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Text type="text1" weight="bold" className="mb-2">
          Lista de Usuarios
        </Text>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Nombre</Text>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Email</Text>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Rol</Text>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Estado</Text>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Text>Fecha Creación</Text>
              </th>
              {canManageUsers && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <Text>Acciones</Text>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {safeUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Text weight="bold">{user.name}</Text>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Text>{user.email}</Text>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <RoleBadge role={user.role} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={user.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Text type="text3">
                    {new Date(user.created_at).toLocaleDateString('es-ES')}
                  </Text>
                </td>
                {canManageUsers && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Flex gap="small">
                      <Tooltip content="Editar usuario">
                        <IconButton
                          icon={Edit}
                          size="small"
                          onClick={() => onEditUser(user)}
                          ariaLabel="Editar"
                        />
                      </Tooltip>
                      {canDeleteUser(user) && (
                        <Tooltip content="Eliminar usuario">
                          <IconButton
                            icon={Delete}
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            ariaLabel="Eliminar"
                            kind="tertiary"
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <Flex justify="center" className="mt-6">
          <Button onClick={onLoadMore} disabled={loading} kind="tertiary">
            {loading ? (
              <Flex align="center" gap="small">
                <Loader size="small" />
                <Text>Cargando...</Text>
              </Flex>
            ) : (
              'Cargar más usuarios'
            )}
          </Button>
        </Flex>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <Modal show={showDeleteConfirm} onClose={cancelDeleteUser}>
          <ModalHeader>
            <Text type="text1" weight="bold">
              Confirmar Eliminación
            </Text>
          </ModalHeader>
          <ModalContent>
            <Text>
              ¿Estás seguro de que deseas eliminar al usuario{' '}
              <strong>{userToDelete.name}</strong>? Esta acción no se puede
              deshacer.
            </Text>
          </ModalContent>
          <ModalFooter>
            <Flex justify="end" gap="medium">
              <Button
                kind="tertiary"
                onClick={cancelDeleteUser}
                disabled={isDeleting}
              >
                Cancelar
              </Button>
              <Button
                kind="primary"
                onClick={confirmDeleteUser}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Flex align="center" gap="small">
                    <Loader size="small" />
                    <Text>Eliminando...</Text>
                  </Flex>
                ) : (
                  'Eliminar Usuario'
                )}
              </Button>
            </Flex>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
